import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { adminSectionsFor } from '@/admin-area/place/admin-sections';
import { useAdministrationScopesOptional } from '@/admin-area/place/AdministrationScopesProvider';
import { useActiveContext } from '@/auth/ActiveContextProvider';
import { useAuth } from '@/auth/AuthProvider';
import { useIsOperator } from '@/auth/OperatorProvider';
import { isWorkspaceEmail } from '@/auth/workspace-domain';
import { useCallerPermissions } from '@/clinical-study/useCallerPermissions';
import { useFinanceCapability } from '@/finance/useFinanceCapability';
import { useArchivePermissions } from '@/archive/use-archive-permissions';
import { useCommunicationsPermissions } from '@/communications/use-communications-permissions';
import { useExpensesPermissions } from '@/expenses/use-expenses-permissions';
import { useToolsPermissions } from '@/tools/use-tools-permissions';
import { useAttentionCounts } from '../counters/use-attention-counts';
import { moduleRegistry, visibleModules } from '../navigation';
import { findActiveLocation, type ActiveLocation } from './active-location';
import {
  resolveAuxiliaryScreens,
  resolveModules,
  withCounts,
  type ResolvedAuxiliaryScreen,
  type ResolvedModule,
} from './resolve-navigation';

/**
 * The one place the shell reads its providers. Everything downstream of it — rail, bottom bar,
 * breadcrumb, command palette — takes plain data and mounts no provider of its own, which is why
 * each of those components is testable by passing it an array.
 *
 * The four gate sources, and why each is what it is:
 *   `isWorkspaceEmail(user.email)` — the account's own domain (SL-0045 Activity 10).
 *   `useIsOperator()`             — the platform operator (SL-0023).
 *   `useFinanceCapability()`      — the entitlement + role read (SL-0053), with
 *                                   `requireActiveContext: false` so the entry can appear for an
 *                                   account that has not chosen a context yet.
 *   `visibleModules(moduleRegistry, …)` — the gated-navigation filter (FR-6.1/FR-6.2) over the
 *                                   registry, which is still empty. Empty capability sets are
 *                                   passed EXPLICITLY rather than defaulted: there is no
 *                                   capability-fetching plumbing yet, and a default would hide
 *                                   that fact.
 *   `useCallerPermissions()`      — the clinical-study module's own effective-permission read,
 *                                   connected to navigation on 2026-09-14. It is per
 *                                   ORGANIZATION, and it is guarded by the very permission it
 *                                   reports — so an account holding no clinical-study role is
 *                                   refused the read and the entry disappears. Fail-closed by
 *                                   construction rather than by a rule someone must remember.
 */
export type NavigationModel = {
  readonly modules: readonly ResolvedModule[];
  readonly auxiliaryScreens: readonly ResolvedAuxiliaryScreen[];
  readonly active: ActiveLocation;
  /** The active organization, so the caller can key the nav on it (FR-6.4). */
  readonly organizationId: string | undefined;
  /**
   * Every gate source above has answered (whatever the answer), so `modules` is the complete nav
   * and will not grow by itself. The start screen waits for this (#430): the nav appears whole.
   * The counters are not gates and are not waited for.
   */
  readonly settled: boolean;
};

type Sources = {
  readonly clinicalStudyPermissions: ReturnType<typeof useCallerPermissions>;
  readonly financeCapability: ReturnType<typeof useFinanceCapability>;
  readonly toolsPermissions: ReturnType<typeof useToolsPermissions>;
  readonly expensesPermissions: ReturnType<typeof useExpensesPermissions>;
  readonly communicationsPermissions: ReturnType<typeof useCommunicationsPermissions>;
  readonly archivePermissions: ReturnType<typeof useArchivePermissions>;
};

/** Every permission code the four reads have resolved: only a RESOLVED read contributes. */
function heldPermissionSet(sources: Sources): Set<string> {
  const {
    clinicalStudyPermissions,
    financeCapability,
    toolsPermissions,
    expensesPermissions,
    communicationsPermissions,
    archivePermissions,
  } = sources;
  return new Set<string>([
    ...(clinicalStudyPermissions.status === 'ready'
      ? (clinicalStudyPermissions.permissions ?? [])
      : []),
    ...(financeCapability.status === 'allowed' ? (financeCapability.permissions ?? []) : []),
    ...(toolsPermissions.status === 'ready' ? toolsPermissions.permissions : []),
    ...(expensesPermissions.status === 'ready' ? expensesPermissions.permissions : []),
    ...(communicationsPermissions.status === 'ready' ? communicationsPermissions.permissions : []),
    ...(archivePermissions.status === 'ready' ? archivePermissions.permissions : []),
  ]);
}

/** Every gate source has answered, whatever the answer. */
function isSettled(
  sources: Sources & {
    readonly operatorStatus: string;
    readonly contextStatus: string;
    readonly administration: ReturnType<typeof useAdministrationScopesOptional>;
  },
): boolean {
  return (
    sources.operatorStatus !== 'loading' &&
    sources.contextStatus !== 'loading' &&
    sources.financeCapability.status !== 'checking' &&
    sources.clinicalStudyPermissions.status !== 'loading' &&
    sources.toolsPermissions.status !== 'loading' &&
    sources.expensesPermissions.status !== 'loading' &&
    sources.communicationsPermissions.status !== 'loading' &&
    sources.archivePermissions.status !== 'loading' &&
    sources.administration?.status !== 'loading'
  );
}

function useRegistryEntitled() {
  return useMemo(
    () =>
      visibleModules(moduleRegistry, {
        entitledModuleIds: new Set<string>(),
        heldPermissions: new Set<string>(),
      }),
    [],
  );
}

function useAdminSections(
  administration: ReturnType<typeof useAdministrationScopesOptional>,
  pathname: string,
  t: TFunction<'shell'>,
) {
  return useMemo(
    () =>
      administration === undefined
        ? undefined
        : adminSectionsFor(pathname, administration.scopes, administration.places, t),
    [administration, pathname, t],
  );
}

export function useNavigationModel(): NavigationModel {
  const { user } = useAuth();
  const { isOperator, status: operatorStatus } = useIsOperator();
  const { active: activeContext, status: contextStatus } = useActiveContext();
  const financeCapability = useFinanceCapability({ requireActiveContext: false });
  // The clinical-study module's own "what may I do here" read. It already existed and two pages
  // in that module already consumed it; connecting it to navigation is what stops the rail
  // offering a module the server will refuse.
  const clinicalStudyPermissions = useCallerPermissions();
  // Strumenti's own read (#387 PR-5): the rail entry's `tools:use` gate.
  const toolsPermissions = useToolsPermissions();
  // «Note spese»'s own read: the rail entry's `expenses:expense:submit` / `read-all` gate.
  const expensesPermissions = useExpensesPermissions();
  // «Comunicazioni»'s own read: the rail entry's `communications:approve` gate.
  const communicationsPermissions = useCommunicationsPermissions();
  // «Archivio»'s own read: the rail entry's `archivio:*:read` gate.
  const archivePermissions = useArchivePermissions();
  const { t } = useTranslation('shell');
  const { pathname } = useLocation();
  // The administration area's own read (FR-AD-3). Optional, because a screen can mount the shell
  // without it in tests; absent means not known, and the `administrator` gate stays closed.
  const administration = useAdministrationScopesOptional();
  const isAdministrator = administration?.status === 'ready' && administration.scopes.length > 0;

  // `t` is a new function identity on every i18next store change, which is exactly when the labels
  // must be recomputed, so it belongs in the dependency list rather than being worked around.
  const registryEntitled = useRegistryEntitled();

  // The admin entry's sections follow the place in the URL and what the caller may do there
  // (FR-AD-2), so they cannot live in the static catalog.
  const adminSections = useAdminSections(administration, pathname, t);

  const modules = useMemo(
    () =>
      resolveModules(
        {
          isWorkspaceAccount: isWorkspaceEmail(user?.email),
          isOperator,
          isAdministrator,
          financeAllowed: financeCapability.status === 'allowed',
          /*
           * Three modules report their own codes now, and the codes are namespaced, so one set
           * serves both.
           *
           * Only a RESOLVED read contributes. Loading and error contribute nothing, so a
           * `permission` gate closes in both — the same fail-closed direction `canUse` takes
           * inside the clinical-study pages, and for the same reason: hiding an entry from
           * someone entitled costs them a reload, whereas showing one that cannot work costs
           * them a red error page and the belief that the product is broken.
           *
           * Each source is coalesced before spreading. `new Set(undefined)` is a legal empty
           * set and `[...undefined]` throws, so merging two sources by spread is strictly more
           * brittle than the single `new Set(x)` this replaced — and it threw for real, inside
           * `Shell`, against a permission read whose resolved state carried no list.
           */
          heldPermissions: heldPermissionSet({
            clinicalStudyPermissions,
            financeCapability,
            toolsPermissions,
            expensesPermissions,
            communicationsPermissions,
            archivePermissions,
          }),
          // The registry filter has already run above; re-expressing its result as capability sets
          // would be a second source of truth, so the resolved ids are fed back in as the
          // entitlement set and the permission set the registry gate will test against.
          capabilities: {
            entitledModuleIds: new Set(registryEntitled.map((entry) => entry.moduleId)),
            heldPermissions: new Set(registryEntitled.map((entry) => entry.requiredPermission)),
          },
        },
        t,
        undefined,
        adminSections === undefined ? {} : { admin: adminSections },
      ),
    [
      user?.email,
      isOperator,
      isAdministrator,
      financeCapability,
      clinicalStudyPermissions,
      toolsPermissions,
      expensesPermissions,
      communicationsPermissions,
      archivePermissions,
      registryEntitled,
      adminSections,
      t,
    ],
  );

  const auxiliaryScreens = useMemo(() => resolveAuxiliaryScreens(t), [t]);

  // Counters are attached AFTER gating, over the already-filtered list, so a counter can never
  // reintroduce a module the gates removed — the arriving number lands on a module that is on
  // screen, or on nothing at all.
  const counts = useAttentionCounts(activeContext?.organizationId);
  const countedModules = useMemo(() => withCounts(modules, counts), [modules, counts]);

  // Active location is computed from the UNCOUNTED list on purpose: it depends on paths only, and
  // recomputing it every time a badge changes would churn the breadcrumb for no reason.
  const active = useMemo(
    () => findActiveLocation(modules, auxiliaryScreens, pathname),
    [modules, auxiliaryScreens, pathname],
  );

  const settled = isSettled({
    operatorStatus,
    contextStatus,
    financeCapability,
    clinicalStudyPermissions,
    toolsPermissions,
    expensesPermissions,
    communicationsPermissions,
    archivePermissions,
    administration,
  });

  return {
    modules: countedModules,
    auxiliaryScreens,
    active,
    organizationId: activeContext?.organizationId,
    settled,
  };
}
